import { useCallback, useContext, useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { toastr } from 'react-redux-toastr';
import { useQueryClient } from 'react-query';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Box } from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { useConfirmModal } from '@fortress-technology-solutions/fortress-component-library/Molecules_Fortress';
import { AppContext } from '../App/context';
import useAssignUnitMutation from '../../hooks/data-modifying/useAssignUnitMutation';
import useCompleteUnitTransferSetupMutation from '../../hooks/data-modifying/useCompleteUnitTransferSetupMutation';
import useTransferLeaseMutation from '../../hooks/data-modifying/useTransferLeaseMutation';
import useUnAssignUnitMutation from '../../hooks/data-modifying/useUnAssignUnitMutation';
import messages from '../ManageUnitAvailability/messages';
import appMessages from '../../containers/App/messages';
import residentMessages from '../ResidentProfile/messages';
import useSelectedProperty from '../../hooks/useSelectedProperty';
import useSearchParams from './hooks.useSearchParams';
import useHistory from '../../hooks/useHistory';
import useIntl from '../../hooks/useIntl';
import { getUrlWithSelectedPropertyId } from '../../utils/navigation-helpers';
import { TAB_KEYS } from '../ResidentProfile/constants';

const useAssignUnit = ({ applicantToAssignUnit }) => {
  const history = useHistory();
  const intl = useIntl();
  const queryClient = useQueryClient();
  const [unitIdToAssign, setUnitIdToAssign] = useState(null);
  const [actionType, setActionType] = useState(null);
  // The unit whose confirmation is open while its background re-check runs; cleared on close so a
  // late re-check does not act on a dialog that is already gone.
  const unitBeingCheckedRef = useRef(null);
  const { openConfirm, openConfirmModal, closeConfirmModal } =
    useConfirmModal();
  const { organizationId, id: propertyId } = useSelectedProperty();
  const { userId } = useContext(AppContext);
  const { isTransfer, applicationId, residentId, isOverride } =
    useSearchParams();
  const { unitTransfers: unitTransfersFlag } = useFlags();
  const householdId = applicantToAssignUnit?.householdId ?? '';

  // Must match the grid's own key (useManageUnitAvailabilityQuery), or reloading it does nothing and
  // the grid keeps showing a unit as free after it was assigned.
  const unitGridQueryKey = useMemo(
    () => ['useManageUnitAvailabilityQuery', organizationId, propertyId],
    [organizationId, propertyId],
  );

  const onClose = useCallback(() => {
    unitBeingCheckedRef.current = null;
    closeConfirmModal();
    setUnitIdToAssign(null);
  }, [closeConfirmModal]);

  const inValidateQueries = useCallback(() => {
    queryClient.invalidateQueries(unitGridQueryKey);
    queryClient.invalidateQueries([
      'applicationService.getApplication',
      organizationId,
      propertyId,
      applicationId,
    ]);
    queryClient.invalidateQueries([
      'household',
      'lease',
      'validateTransferLease',
    ]);
  }, [
    applicationId,
    organizationId,
    propertyId,
    queryClient,
    unitGridQueryKey,
  ]);

  const { mutate: assignUnitMutate, isLoading: assignUnitMutateIsLoading } =
    useAssignUnitMutation({
      organizationId,
      propertyId,
      applicationId: applicantToAssignUnit?.id,
      isOverride: isOverride === 'true',
      options: {
        onSuccess: () => {
          inValidateQueries();
          toastr.success(
            intl.formatMessage(appMessages.success),
            intl.formatMessage(messages.assignUnitSuccess),
          );
          onClose();
        },
        onError: (err) => {
          // The unit can be taken by someone else while this page is open (another user, or the
          // portal after an applicant's fee payment), and the server then refuses the assignment.
          // Reload the grid so the unit shows as taken and Assign is disabled, instead of leaving
          // a stale row and an open confirmation behind.
          inValidateQueries();
          onClose();
          toastr.error(intl.formatMessage(appMessages.error), err.toString());
        },
      },
    });

  const { mutate: unAssignUnitMutate, isLoading: unAssignUnitMutateIsLoading } =
    useUnAssignUnitMutation({
      organizationId,
      propertyId,
      assignedUnitId: applicantToAssignUnit?.au?.id,
      options: {
        onSuccess: () => {
          inValidateQueries();
          toastr.success(
            intl.formatMessage(appMessages.success),
            intl.formatMessage(messages.unitUnassigned),
          );
          onClose();
        },
        onError: (err) => {
          toastr.error(intl.formatMessage(appMessages.error), err.toString());
        },
      },
    });

  const {
    mutate: transferLeaseMutate,
    isLoading: transferLeaseMutateIsLoading,
  } = useTransferLeaseMutation({
    organizationId,
    propertyId,
    residentId,
    applicationId,
    userId,
    options: {
      onSuccess: () => {
        inValidateQueries();
        toastr.success(
          intl.formatMessage(appMessages.success),
          intl.formatMessage(residentMessages.successDescriptionTransfer),
        );
        onClose();

        history.push(
          getUrlWithSelectedPropertyId(
            `/resident/${residentId}?tab=${TAB_KEYS.LEASE_DATA}`,
          ),
        );
      },
      onError: (err) => {
        toastr.error(intl.formatMessage(appMessages.error), err.toString());
      },
    },
  });

  const {
    mutate: completeUnitTransferSetupMutate,
    isLoading: completeUnitTransferSetupMutateIsLoading,
  } = useCompleteUnitTransferSetupMutation({
    organizationId,
    propertyId,
    householdId,
    applicationId,
    options: {
      onSuccess: () => {
        inValidateQueries();
        queryClient.invalidateQueries([
          'startTransferEligibility',
          organizationId,
          propertyId,
          householdId,
        ]);
        // Invalidate the certifications tab caches so the newly started transfer
        // certification(s) are pulled when the user opens the Qualification History tab.
        queryClient.invalidateQueries([
          'getHouseholdProfileAffordableQualificationsByProgram',
        ]);
        queryClient.invalidateQueries(['getQualificationHistory']);
        toastr.success(
          intl.formatMessage(appMessages.success),
          intl.formatMessage(residentMessages.successDescriptionTransfer),
        );
        onClose();

        history.push(
          getUrlWithSelectedPropertyId(
            `/resident/${residentId}?tab=${TAB_KEYS.LEASE_DATA}`,
          ),
        );
      },
      onError: (err) => {
        toastr.error(intl.formatMessage(appMessages.error), err.toString());
      },
    },
  });

  const assignUnit = useCallback(() => {
    if (isTransfer && residentId) {
      if (unitTransfersFlag) {
        completeUnitTransferSetupMutate(unitIdToAssign);
      } else {
        transferLeaseMutate(unitIdToAssign);
      }
    } else {
      assignUnitMutate(unitIdToAssign);
    }
  }, [
    assignUnitMutate,
    completeUnitTransferSetupMutate,
    isTransfer,
    residentId,
    unitIdToAssign,
    transferLeaseMutate,
    unitTransfersFlag,
  ]);

  const handleAssignUnitClick = useCallback(
    (unitId) => {
      setActionType('assign');
      openConfirmModal();
      setUnitIdToAssign(unitId);
      if (isTransfer) {
        return Promise.resolve();
      }
      // The grid shows the units as they were when the page loaded. Another user, or the portal
      // after an applicant's fee payment, may have taken this unit since. Reload the grid in the
      // background (it can be slow, so the confirmation is not held for it) and, if the unit now
      // belongs to another application (the same rule that disables Assign in the grid), close the
      // confirmation and say why. Confirming before this returns is still refused by the server.
      unitBeingCheckedRef.current = unitId;
      return queryClient
        .refetchQueries(unitGridQueryKey, { exact: true })
        .then(() => {
          if (unitBeingCheckedRef.current !== unitId) {
            return;
          }
          unitBeingCheckedRef.current = null;
          const units = queryClient.getQueryData(unitGridQueryKey)?.units ?? [];
          const holderApplicationId = units.find((unit) => unit.id === unitId)
            ?.nextAssignation?.application?.id;
          if (
            holderApplicationId &&
            holderApplicationId !== applicantToAssignUnit?.id
          ) {
            onClose();
            toastr.error(
              intl.formatMessage(appMessages.error),
              intl.formatMessage(messages.unitJustAssigned),
            );
          }
        });
    },
    [
      applicantToAssignUnit?.id,
      intl,
      isTransfer,
      onClose,
      openConfirmModal,
      queryClient,
      unitGridQueryKey,
    ],
  );

  const handleUnAssignUnitClick = useCallback(() => {
    setActionType('unassign');
    openConfirmModal();
  }, [openConfirmModal]);

  const { firstName, lastName } = applicantToAssignUnit?.prospectInfo ?? {};

  return {
    title: (
      <>
        <FormattedMessage
          {...messages[
            actionType === 'unassign'
              ? 'confirmUnassignUnit'
              : isTransfer
              ? 'confirmTransferUnit'
              : 'confirmAssignUnit'
          ]}
        />
        <Box paddingLeft={0.5}> {`${firstName} ${lastName}`}</Box>
      </>
    ),
    onClose,
    actionsProps: [
      {
        variant: 'text',
        children: <FormattedMessage {...appMessages.cancel} />,
        onClick: onClose,
      },
      {
        children: <FormattedMessage {...appMessages.yes} />,
        submitButton: true,
        isSubmitting:
          assignUnitMutateIsLoading ||
          completeUnitTransferSetupMutateIsLoading ||
          transferLeaseMutateIsLoading ||
          unAssignUnitMutateIsLoading,
        onClick: { assign: assignUnit, unassign: unAssignUnitMutate }[
          actionType
        ],
      },
    ],
    handleAssignUnitClick,
    handleUnAssignUnitClick,
    openConfirm,
    openConfirmModal,
    closeConfirmModal,
  };
};

export default useAssignUnit;
